import React, { useCallback } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import { useReadOnly } from 'slate-react';
import type { ComponentProps, LinkElement } from '../../../types/aha-editor';
import { normalizeHref } from '../utils';
import useAhaEditorFocused from '../../../components/useAhaEditorFocused';

const isSameHost = (href: string) => {
  try {
    return new URL(href, location.href).origin === location.origin;
  } catch (e) {
    return false;
  }
};

const LinkComponent: React.FC<ComponentProps<LinkElement>> = ({
  attributes,
  transformedElement: element,
  children,
}) => {
  const href = element.link_href || '#';
  const normalizedHref = normalizeHref(href);
  const extraAttrs: { target?: string; rel?: string } = {};
  const linkTarget = element.link_target;

  if (href[0] !== '#') {
    if (element.link_target !== '_self') {
      extraAttrs.target = '_blank';
    }

    extraAttrs.rel = 'noopener noreferrer';
  }

  // If the editor is not focused then open the link directly rather than
  // activating the element toolbar. If the link is for the same domain then
  // open it in the same tab, otherwise open it in a new tab.
  const focused = useAhaEditorFocused();
  const readOnly = useReadOnly();
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Let the mouse down through to focus on the link/show the toolbar
      if (focused || readOnly) {
        return;
      }

      // Force open the link in a new tab
      // We can't rely on the browser's default behaviour here, because if we don't prevent the default, the
      // editor will become focused instead of opening the link.
      if (e.metaKey || e.ctrlKey) {
        window.open(normalizedHref, '_blank');
        e.preventDefault();
        return;
      }

      // Since we're not focused, don't let the editor become focused, then fall through to default click handler.
      if (
        // If the link element explicitly has a target, we will use it
        !linkTarget &&
        href !== '#' &&
        (href[0] === '#' || isSameHost(normalizedHref))
      ) {
        e.preventDefault();
        return;
      }

      // Open the link in a new tab.
      window.open(normalizedHref, '_blank');
      e.preventDefault();
      e.stopPropagation();
    },
    [focused, readOnly, linkTarget]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (readOnly) {
        // Read-only editors should act as normal documents, so fall
        // back to the default behavior
        return;
      }

      // Prevent the click in focused state so we show the toolbar
      // instead of opening the link.
      if (focused) {
        // We must preventDefault to stop Turbolinks from following it
        e.preventDefault();
        return;
      }

      // If there's no URL, do nothing
      if (href === '#') {
        e.preventDefault();
        return;
      }

      if (href !== '#' && (href[0] === '#' || isSameHost(normalizedHref))) {
        // Navigate to the link in this tab.
        if (isSameHost(normalizedHref) && href.includes('#')) {
          try {
            const url = new URL(normalizedHref, window.location.href);
            if (
              url.origin === window.location.origin &&
              url.pathname === window.location.pathname
            ) {
              window.location.href = url.hash;
            }
          } catch (e) {
            console.error(e);
          }
        } else if (window.Turbolinks) {
          window.Turbolinks.visit(normalizedHref);
        } else {
          window.location.assign(normalizedHref);
        }
      } else {
        // Open the link in a new tab.
        window.open(normalizedHref, '_blank');
      }
    },
    [focused, readOnly]
  );

  return (
    <a
      href={element.link_href}
      {...attributes}
      {...extraAttrs}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {children}
    </a>
  );
};

export default ApplicationView(LinkComponent);
